import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ClientResponse, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';

interface ClientsState {
  clients: ClientResponse[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheInfo: CacheInfo | null;
  setClients: (clients: ClientResponse[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchClients: (accessToken: string, logout: () => void, forceRefresh?: boolean) => Promise<void>;
}

export const useClientsStore = create<ClientsState>()(devtools((set, get) => ({
  clients: [],
  loading: false,
  error: null,
  lastFetched: null,
  cacheInfo: null,
  setClients: (clients) => set({ clients, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  fetchClients: async (accessToken: string, logout: () => void, forceRefresh = false) => {
    const state = get();

    // If we have cached data and not forcing refresh, skip the fetch
    if (!forceRefresh && state.clients.length > 0 && state.lastFetched) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/clients`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch clients: ${response.statusText}`);
      }

      const data = await response.json();
      set({
        clients: data.clients,
        loading: false,
        lastFetched: Date.now(),
        cacheInfo: data.cacheInfo ?? null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'clients-store' }));
